import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { BedDouble, Users, User } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("id-ID").format(Math.round(n));

// Default markup rules (based on standard umrah industry pricing)
const ROOM_MARKUP = {
  quad:   { addIdr: 0,       description_id: "Kamar untuk 4 jamaah, paling ekonomis", description_en: "Room for 4 pilgrims, most economical" },
  triple: { addIdr: 3_000_000, description_id: "Kamar untuk 3 jamaah, ruang lebih lega",  description_en: "Room for 3 pilgrims, more spacious" },
  double: { addIdr: 6_000_000, description_id: "Kamar untuk 2 jamaah, paling privat",     description_en: "Room for 2 pilgrims, most private" },
};

const ROOMS = [
  { key: "quad",   Icon: Users,      label_id: "Kamar Quad",   label_en: "Quad Room",   badge_id: "Terpopuler", badge_en: "Most Popular" },
  { key: "triple", Icon: Users,      label_id: "Kamar Triple", label_en: "Triple Room" },
  { key: "double", Icon: BedDouble,  label_id: "Kamar Double", label_en: "Double Room", badge_id: "Paling Privat", badge_en: "Most Private" },
];

export default function RoomPricingTable({ priceIdr, selectedRoom, onSelectRoom }) {
  const { lang } = useLang();
  const t = (id, en) => (lang === "id" ? id : en);
  const base = priceIdr || 0;

  return (
    <section className="mt-16 bg-card border border-border rounded-lg p-8 lg:p-10" data-testid="room-pricing-table">
      <div className="mb-8">
        <p className="overline mb-3">{t("Pilihan Kamar", "Room Options")}</p>
        <h3 className="font-serif text-3xl text-primary">
          {t("Harga per Tipe Kamar", "Price per Room Type")}
        </h3>
        <p className="text-sm text-muted-foreground mt-2">
          {t("Pilih tipe kamar sesuai preferensi Anda dan keluarga.", "Choose the room type that suits you and your family.")}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
        {ROOMS.map(({ key, Icon, label_id, label_en, badge_id, badge_en }) => {
          const price = base + ROOM_MARKUP[key].addIdr;
          const selected = selectedRoom === key;
          const disabled = base === 0;
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onSelectRoom && onSelectRoom(key)}
              data-testid={`room-option-${key}`}
              className={`relative text-left p-6 rounded-xl border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                selected
                  ? "border-primary bg-primary/5 shadow-md"
                  : "border-border hover:border-accent hover:shadow-sm"
              }`}
            >
              {(badge_id || badge_en) && (
                <span className="absolute -top-2.5 right-4 bg-accent text-primary text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                  {t(badge_id, badge_en)}
                </span>
              )}
              <Icon className={`h-5 w-5 mb-3 ${selected ? "text-primary" : "text-accent"}`} />
              <p className="font-serif text-lg text-primary">{t(label_id, label_en)}</p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                {t(ROOM_MARKUP[key].description_id, ROOM_MARKUP[key].description_en)}
              </p>
              <p className="font-serif text-2xl text-primary">
                {base > 0 ? `Rp ${fmt(price)}` : "—"}
              </p>
              {ROOM_MARKUP[key].addIdr > 0 && base > 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  +Rp {fmt(ROOM_MARKUP[key].addIdr)} {t("dari Quad", "from Quad")}
                </p>
              )}
              {selected && (
                <div className="mt-3 flex items-center gap-1 text-xs text-primary font-semibold">
                  <span className="w-2 h-2 rounded-full bg-primary" /> {t("Dipilih", "Selected")}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-muted-foreground italic">
        * {t("Harga sudah termasuk semua fasilitas pada paket.",
             "Prices include all package facilities.")}
      </p>
    </section>
  );
}

export { ROOM_MARKUP };
